'use client'

import { useEffect, useRef } from 'react'

import { Section, useThemeStore } from '@/components/findex-connect/store/ThemeStore'
import { TestimonialBlockSb } from '@/components/findex-connect/types/component-types-sb'

import { TestimonialCard } from '../TestimonialCard/TestimonialCard'
import { Carousel, CarouselContent, CarouselNext, CarouselPrevious } from '../ui/carousel'

type TestimonialBlockProps = {
  data: TestimonialBlockSb
  sectionId: Section
}

export const TestimonialBlock: React.FC<TestimonialBlockProps> = ({ data, sectionId }) => {
  const sectionRef = useRef(null)
  const activeSection = useThemeStore((state) => state.activeSection)

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.8,
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          useThemeStore.setState({
            activeSection: sectionId,
          })
        }
      })
    }, observerOptions)

    if (sectionRef?.current) {
      observer.observe(sectionRef.current)
    }
  }, [sectionRef, sectionId, activeSection])

  // Data
  if (!data) return null
  const { heading, testimonials } = data
  if (!testimonials || !heading) return null

  return (
    <div className="TESTIMONIAL-BLOCK-CONTAINER mx-auto w-full bg-white" id={sectionId}>
      <div className="TESTIMONIAL-BLOCK-INNER mx-auto flex w-full flex-col overflow-hidden py-10 md:py-20">
        <div className="TESTIMONIAL-BLOCK-HEADER max-w-container p-gutter mx-auto mb-3 flex w-full md:mb-8">
          <h2 className="mb-2 p-1 md:mb-6" tabIndex={0} ref={sectionRef}>
            {heading}
          </h2>
        </div>
        <div className="CAROUSEL-CONTAINER max-w-container mx-auto w-full overflow-hidden md:overflow-visible">
          <Carousel className="CAROUSEL" opts={{ align: 'start' }}>
            <CarouselPrevious className="border-subtle text-subtle top-[95%] left-6 z-10 size-10 cursor-pointer bg-white/0 hover:scale-105 hover:bg-white/0 md:-top-21 md:right-23 md:left-[unset]" />
            <CarouselNext className="border-subtle text-subtle top-[95%] left-19 z-10 size-10 cursor-pointer bg-white/0 hover:scale-105 hover:bg-white/0 md:-top-21 md:right-10 md:left-[unset]" />

            <CarouselContent className="CAROUSEL-CONTENT ml-6 gap-6 pb-26 md:ml-10 md:gap-10 md:overflow-visible md:pb-2 lg:ml-12">
              {testimonials?.map((item, index, allItems) => {
                return (
                  <TestimonialCard
                    {...item}
                    key={`${index}-${item.customerName}`}
                    isLastItem={index === allItems.length - 1}
                  />
                )
              })}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </div>
  )
}
